import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Button, Space, Tag, Avatar, Input, Select, Empty, Dropdown, Tooltip } from 'antd';
import { ProjectOutlined, PlusOutlined, SearchOutlined, FilterOutlined, MoreOutlined, EditOutlined, DeleteOutlined, UploadOutlined, EyeOutlined, FilePdfOutlined, DownloadOutlined } from '@ant-design/icons';
import DeleteConfirmModal from './DeleteConfirmModal';
import EmptyStateIllustrations from './EmptyStateIllustrations';
import '../styles/app-redesign.css';

const { Search } = Input;

const ProjectsGrid = React.memo(({ projects, onCreateProject, onViewProject, onUploadToProject, onDeleteProject }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && project.is_active) ||
                         (filterStatus === 'inactive' && !project.is_active);
    return matchesSearch && matchesFilter;
  });

  const getProjectActions = (project) => ({
    items: [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'View Details',
        onClick: () => onViewProject(project)
      },
      {
        key: 'upload',
        icon: <UploadOutlined />,
        label: 'Upload PDF',
        onClick: () => onUploadToProject(project)
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Edit Project',
      },
      // Visual separator + danger item for Delete. Regression fix:
      // the handleDeleteClick handler and DeleteConfirmModal were
      // already wired through to App.handleDeleteProject, but this
      // menu entry was missing so there was no way to trigger them.
      { type: 'divider' },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete Project',
        danger: true,
        onClick: () => handleDeleteClick(project),
      },
    ]
  });

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (projectToDelete) {
      onDeleteProject(projectToDelete);
      setDeleteModalVisible(false);
      setProjectToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setProjectToDelete(null);
  };

  return (
    <div className="app-content-redesign">
      {/* Header */}
      <div className="dashboard-header-redesign">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h1 className="dashboard-title">Projects</h1>
            <p className="dashboard-subtitle">
              Construction project management and document tracking
            </p>
          </div>
          <button 
            className="section-action-btn"
            onClick={onCreateProject}
          >
            <PlusOutlined />
            <span>New Project</span>
          </button>
        </div>

        {/* Search and Filters */}
        <Row gutter={16}>
          <Col flex="auto">
            <Search
              placeholder="Search projects by name or location..."
              prefix={<SearchOutlined style={{ color: '#a3a3a3' }} />}
              size="large"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              size="large"
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 160 }}
              suffixIcon={<FilterOutlined />}
            >
              <Select.Option value="all">All Projects</Select.Option>
              <Select.Option value="active">Active Only</Select.Option>
              <Select.Option value="inactive">Inactive Only</Select.Option>
            </Select>
          </Col>
        </Row>
      </div>

      {/* Projects Grid - Blueprint Style */}
      {filteredProjects.length > 0 ? (
        <div className="projects-grid-redesign">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="project-card-redesign"
              onClick={() => onViewProject(project)}
            >
              <div className="project-card-header">
                <div>
                  <h3 className="project-card-title">{project.name}</h3>
                  <div className="project-card-client">
                    {project.location || 'No location specified'}
                  </div>
                  {project.created_by_name && (
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                      Created by {project.created_by_name}
                    </div>
                  )}
                </div>
                <div className={`project-status-badge ${project.is_active ? 'active' : 'inactive'}`}>
                  {project.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
              
              <div className="project-card-stats">
                <div className="project-stat-item">
                  <div className="project-stat-value">{project.documents?.length || 0}</div>
                  <div className="project-stat-label">Documents</div>
                </div>
                <div className="project-stat-item">
                  <div className="project-stat-value">{project.line_items_count || 0}</div>
                  <div className="project-stat-label">Line Items</div>
                </div>
              </div>
              
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(0,0,0,0.08)', display: 'flex', gap: '8px' }}>
                <Button 
                  size="small"
                  icon={<UploadOutlined />}
                  onClick={(e) => { e.stopPropagation(); onUploadToProject(project); }}
                  style={{ flex: 1 }}
                >
                  Upload
                </Button>
                <Dropdown menu={getProjectActions(project)} trigger={['click']}>
                  <Button 
                    size="small"
                    icon={<MoreOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Dropdown>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state-redesign">
          <div className="empty-state-icon">
            <ProjectOutlined />
          </div>
          <h3 className="empty-state-title">No Projects Yet</h3>
          <p className="empty-state-description">
            Create your first construction project to get started with document management and takeoff extraction.
          </p>
          <button className="section-action-btn" onClick={onCreateProject}>
            <PlusOutlined />
            <span>Create Project</span>
          </button>
        </div>
      )}

      <DeleteConfirmModal
        visible={deleteModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        itemName={projectToDelete?.name}
        itemType="project"
      />
    </div>
  );
});

ProjectsGrid.displayName = 'ProjectsGrid';

export default ProjectsGrid;
